/**
 * Visualizing abilities (2026-08-26) — user request: "we also need to
 * visualize the abilities and spells in-game."
 *
 * One identity per ability: a painted icon, a fallback glyph, a colour, and a
 * world-effect archetype.
 *
 * Before this file, `character-visual.ts`'s `playAbilityVfx()` drew the same
 * expanding yellow ring at the caster's feet for all fourteen abilities —
 * its own comment said so ("generic, class-agnostic pulse... no per-ability
 * art yet") — and the action bar drew fourteen identical grey squares
 * separated only by 9px of text. A Trinket, a Cleave and a Mend were
 * indistinguishable in the world and nearly so on the bar.
 *
 * **The icon and the world effect share a colour on purpose.** The player
 * learns an ability as a coloured mark on the bar, and that same colour is
 * what blooms in the room when they press it — so the bar teaches the world
 * and the world reinforces the bar. This is the whole reason the two live in
 * one table instead of a UI icon set and a separate VFX config.
 *
 * Client-side mirror of `backend/src/game/dungeon-instance/config/ability-defs.ts`,
 * kept in sync by hand — same convention as `config/character-roster.ts` and
 * `config/tuning.ts`. Only the ids are mirrored: everything else here is
 * presentation, which is correctly the client's business. Nothing that
 * affects the simulation is duplicated — a radius, a duration and who was hit
 * all arrive on the `ability.used` message, precisely so a talent that scales
 * one of them cannot make the drawing disagree with the fight.
 */

/**
 * The nine shapes an ability effect can take in the world.
 *
 * Archetypes rather than fourteen bespoke effects: the shape says what *kind*
 * of thing happened (something was struck / a radius was swept / a body is
 * held), which is the part the player has to read in a fraction of a second,
 * and the colour says which ability did it. Fourteen unique effects would be
 * fourteen things to recognise; nine shapes in ten colours is two much
 * smaller lessons.
 */
export type AbilityFxKind =
  /** A shell that closes over the caster and holds for the effect's duration. Defensive states. */
  | 'WARD'
  /** An inward collapse and a dimming of the body. Vanish, and any future stealth. */
  | 'SHROUD'
  /** A ring racing outward to the effect's true radius. Shouts and pulses. */
  | 'SHOUT'
  /** An arc sweeping around the caster to the effect's true radius. Weapon AoE. */
  | 'SWEEP'
  /** A burst on the subject, at the moment of contact. Targeted weapon abilities. */
  | 'STRIKE'
  /** A mote travelling caster → subject, and a burst where it lands. The one ranged spell. */
  | 'BOLT'
  /** Motes rising off the subject. Heals. */
  | 'BLESSING'
  /** A cage on the ground that holds for the effect's duration. Roots. */
  | 'SNARE'
  /** A line drawn caster → subject. Effects that change who something is looking at. */
  | 'LINK';

export interface AbilityVisual {
  /**
   * Painted icon, as a basename under `ABILITY_ICON_BASE` — the face of the
   * ability on the action bar.
   *
   * Generated art (2026-08-26) rather than the line glyph that shipped with
   * the first pass: a 52px slot showing a two-stroke drawing reads as a
   * wireframe placeholder next to this game's own talent icons, which are
   * painted plates in the same dark-fantasy register. The bar is the screen
   * the player looks at most, and it was the least finished thing on it.
   *
   * A basename rather than a path so the four trinket ids can share one file
   * (`trinket`) — the same panic button in the same slot for every class,
   * which `ability-defs.ts` calls "the whole point".
   */
  icon: string;
  /**
   * SVG path data in a 24x24 box, stroked with the ability's colour — same
   * shape of icon as `core/auth`'s.
   *
   * Kept, and still drawn, as the icon's **fallback**: it costs a few hundred
   * bytes of path data, needs no network, and an action bar whose art 404s
   * has to stay readable rather than becoming a row of empty squares. The
   * slot swaps to it if the image fails to load.
   */
  glyph: string;
  /** Shared by the glyph on the bar and the effect in the room. */
  colorHex: number;
  fx: AbilityFxKind;
}

/**
 * Colours are per class family, and within a family the ability's *role*
 * picks the shade — a defensive tool is always the pale one, a finisher
 * always the hot one. So `eviscerate` and `reckless_blow` read as "the big
 * one" for a rogue and a barbarian respectively without either player having
 * to learn the other's kit.
 *
 * The Trinket is deliberately the same white for every class: it sits in the
 * same slot with the same cooldown whoever you are playing, and
 * `ability-defs.ts` calls that identical muscle memory "the whole point of a
 * panic button". Giving it four class colours would undo that.
 */
const TRINKET_WHITE = 0xf1f1f4;
const TRINKET_GLYPH = 'M9 6 L6 9 a4 4 0 0 0 6 6 M15 18 l3-3 a4 4 0 0 0-6-6 M4 4 l16 16';

export const ABILITY_VISUALS: Record<string, AbilityVisual> = {
  // ── Knight: steel, warming toward the aggro tools ────────────────────
  brace: {
    icon: 'brace',
    glyph: 'M12 3 L20 6 v6 c0 5-4 8-8 9-4-1-8-4-8-9V6 Z',
    colorHex: 0xa8cdff, fx: 'WARD',
  },
  shield_slam: {
    icon: 'shield_slam',
    glyph: 'M11 3 L18 5.5 v5 c0 4-3 6.5-7 7.5-4-1-7-3.5-7-7.5v-5 Z M17 15 l4 4 M14 19 l3 1 M19 14 l1 3',
    colorHex: 0xffc861, fx: 'STRIKE',
  },
  taunt: {
    icon: 'taunt',
    glyph: 'M3 14 L13 8 L20 4 v16 l-7-4 Z M6 15 v4 h4',
    colorHex: 0xff7a45, fx: 'LINK',
  },

  // ── Rogue: cold poison, hot finisher ─────────────────────────────────
  vanish: {
    icon: 'vanish',
    glyph: 'M12 3 c-5 0-7 4-7 8 v10 h14 V11 c0-4-2-8-7-8 Z M9 12 h2 M13 12 h2',
    colorHex: 0x9d8cd8, fx: 'SHROUD',
  },
  sinister_strike: {
    icon: 'sinister_strike',
    glyph: 'M12 2 L15 8 v7 H9 V8 Z M8 15 h8 M12 15 v7',
    colorHex: 0x6ee7b7, fx: 'STRIKE',
  },
  eviscerate: {
    icon: 'eviscerate',
    glyph: 'M4 18 L18 4 M8 21 L21 8 M3 13 L13 3',
    colorHex: 0xff5c8a, fx: 'STRIKE',
  },

  // ── Barbarian: amber bellow, deepening red as the blow gets heavier ──
  warcry: {
    icon: 'warcry',
    glyph: 'M4 12 h3 M10 8 c2.5 2 2.5 6 0 8 M14 5 c4.5 3 4.5 11 0 14 M18 2 c6.5 4 6.5 16 0 20',
    colorHex: 0xffb347, fx: 'SHOUT',
  },
  cleave: {
    icon: 'cleave',
    glyph: 'M3 17 A11 11 0 0 1 21 17 M7 15 l-3 5 M17 15 l3 5',
    colorHex: 0xff6b4a, fx: 'SWEEP',
  },
  reckless_blow: {
    icon: 'reckless_blow',
    glyph: 'M12 2 v9 M7 7 l5 5 5-5 M4 14 l8 8 8-8',
    colorHex: 0xdc2f2f, fx: 'STRIKE',
  },

  // ── Druid: green for life, moonlight for the one spell that hurts ────
  mend: {
    icon: 'mend',
    glyph: 'M20 4 C10 4 4 10 4 20 14 20 20 14 20 4 Z M5 19 L14 10',
    colorHex: 0x7ee88a, fx: 'BLESSING',
  },
  swiftmend: {
    icon: 'swiftmend',
    glyph: 'M19 5 C11 5 6 10 6 18 14 18 19 13 19 5 Z M3 8 h5 M2 12 h4 M3 16 h3',
    colorHex: 0xc4ff7a, fx: 'BLESSING',
  },
  entangle: {
    icon: 'entangle',
    glyph: 'M12 22 V9 M12 13 C8 13 6 10 6 6 10 6 12 9 12 13 M12 11 c4 0 6-3 6-7-4 0-6 3-6 7 M7 22 h10',
    colorHex: 0x3fa15c, fx: 'SNARE',
  },
  moonfire: {
    icon: 'moonfire',
    glyph: 'M17 3 a9 9 0 1 0 0 18 7.5 7.5 0 0 1 0-18 Z M19 8 l1 2 2 1-2 1-1 2-1-2-2-1 2-1 Z',
    colorHex: 0xa9c2ff, fx: 'BOLT',
  },

  // ── Universal: one panic button, one look ────────────────────────────
  trinket_knight: { icon: 'trinket', glyph: TRINKET_GLYPH, colorHex: TRINKET_WHITE, fx: 'WARD' },
  trinket_rogue: { icon: 'trinket', glyph: TRINKET_GLYPH, colorHex: TRINKET_WHITE, fx: 'WARD' },
  trinket_barbarian: { icon: 'trinket', glyph: TRINKET_GLYPH, colorHex: TRINKET_WHITE, fx: 'WARD' },
  trinket_druid: { icon: 'trinket', glyph: TRINKET_GLYPH, colorHex: TRINKET_WHITE, fx: 'WARD' },
};

/**
 * What an unknown ability id looks like.
 *
 * Not an error and not nothing: an ability added to the backend table before
 * this file catches up must still be visible, because an ability that does
 * something and shows nothing reads to the player as an input that was
 * dropped. This is the old generic pulse, kept for exactly that job.
 */
export const FALLBACK_ABILITY_VISUAL: AbilityVisual = {
  icon: '_unknown',
  glyph: 'M12 4 v16 M4 12 h16',
  colorHex: 0xfff2a8,
  fx: 'SHOUT',
};

export function abilityVisual(abilityId: string): AbilityVisual {
  return ABILITY_VISUALS[abilityId] ?? FALLBACK_ABILITY_VISUAL;
}

/** `0x7ee88a` → `#7ee88a`, for the DOM half of the identity. */
export function abilityCssColor(colorHex: number): string {
  return `#${colorHex.toString(16).padStart(6, '0')}`;
}

/**
 * Where the painted ability icons live.
 *
 * `public/` rather than an `import`ed module asset, and WebP at 160px, for the
 * same reasons the talent icons are: the browser caches them, Vite does not
 * inline them into the entry chunk, and the bar is drawn at 52px so 160 is
 * already twice what a 2x screen asks for. All eighteen together are 136KB —
 * less than one of this game's character models.
 */
export const ABILITY_ICON_BASE = '/assets/ui/abilities';

/** `'brace'` → `/assets/ui/abilities/brace.webp`. */
export function abilityIconUrl(icon: string): string {
  return `${ABILITY_ICON_BASE}/${icon}.webp`;
}

/**
 * The satchel's icons, keyed by the server's consumable ids
 * (`backend/src/game/consumables/config/consumable-defs.ts`).
 *
 * They live here, next to the abilities, because they are drawn on the same
 * bar, in the same slots, sharing the same GCD — and a bar with painted
 * abilities beside two grey squares of text would read as a bar that failed
 * to load half of itself. Not part of `AbilityVisual`: a consumable has no
 * world effect and no colour identity to keep in step, only a face.
 */
export const CONSUMABLE_ICONS: Record<string, string> = {
  salve: 'salve',
  tonic: 'tonic',
  whetstone: 'whetstone',
  // QA 10.10 #19 (2026-10-15): slots 8 and 9 shipped with plates the shop
  // already showed, but this table was never told — so the bar drew "?".
  draught: 'draught',
  poultice: 'poultice',
};

/**
 * A consumable's icon, falling back to the unknown-sigil plate.
 *
 * Same reasoning as `FALLBACK_ABILITY_VISUAL`: a consumable the server ships
 * before this table catches up must still show a face, because a slot that
 * draws nothing reads as a slot that is broken.
 */
export function consumableIconUrl(id: string): string {
  return abilityIconUrl(CONSUMABLE_ICONS[id] ?? '_unknown');
}

/**
 * How long each archetype's effect lives on screen, in ms, when the effect
 * itself has no duration to borrow.
 *
 * A WARD or a SNARE ignores this and runs for the `durationMs` the server
 * reported — those are visualisations of a state that is genuinely still in
 * force, and one that faded early would be a lie about whether the knight is
 * still stagger-immune. Everything else is a flourish over a thing that
 * already happened, and is tuned purely for readability.
 */
export const ABILITY_FX_DURATION_MS: Record<AbilityFxKind, number> = {
  WARD: 900,
  SHROUD: 700,
  SHOUT: 650,
  SWEEP: 450,
  STRIKE: 380,
  BOLT: 260,
  BLESSING: 900,
  SNARE: 1_200,
  LINK: 320,
};
